import React from "react";
import styles from "./../RecordTable.module.scss";
import { isEquivalentValue } from "./Utils";

const SummaryTable = ({
  selectedRecords,
  action,
  attribute,
  correctValue,
  incorrectValue,
  system,
  attrCode,
  code
}) => {
  const GrayValueWrapper = (text, title) => {
    return (
      <span className={styles.noValue} title={title}>
        {text}
      </span>
    );
  };

  const GreenValueWrapper = (text, title) => {
    return (
      <span className={styles.matchingValue} title={title}>
        {text}
      </span>
    );
  };

  // Returns a list of values that will be displayed in the summary table. The values are filtered based on the
  // override_attribute and override_extension_system values.
  const SummaryValueList = () => {
    var returnList = selectedRecords.flat();

    switch (attribute) {
      case "birthDate":
      case "gender":
        return returnList;
        break;

      case "identifier":
        if (code !== "" && code !== undefined) {
          return returnList.filter(
            v => v.get("code") === code && !v.has("system")
          );
        } else {
          return returnList.filter(v => v.get("system") === system);
        }
        break;

      case "address":
      case "name":
      case "phone":
      case "email":
        return returnList.filter(v => v.get("attrCode") === attrCode);
        break;
    }
  };

  const summaryDisplayRecordId = (valueList, record) => {
    const value = valueList.find(
      v => v.get("source") + " | " + v.get("id") === record
    );

    if (typeof value === "undefined") {
      const title =
        "The record does not contain the given attribute and extension/system.";
      return GrayValueWrapper(record, title);
    }
    if (value.get("use") === "old") {
      const title = "The value is inactive.";
      return GrayValueWrapper(record, title);
    }
    if (value.get("overridePresent")) {
      const title =
        "The attribute value has already been overridden. The change will not be applied.";
      return GrayValueWrapper(record, title);
    }
    if (
      !isEquivalentValue(value.get("displayValue"), incorrectValue, attribute)
    ) {
      const title = 'The value does not match the selected "Value to Change".';
      return GrayValueWrapper(record, title);
    }
    return record;
  };

  const summaryDisplayCurrentValue = (valueList, record) => {
    const value = valueList.find(
      v => v.get("source") + " | " + v.get("id") === record
    );

    if (typeof value === "undefined") {
      const title =
        "The record does not contain the given attribute and extension/system.";
      return GrayValueWrapper("[No Value]", title);
    }
    if (value.get("use") === "old") {
      const title = "The value is inactive.";
      return GrayValueWrapper("[Inactive] " + value.get("displayValue"), title);
    }
    if (value.get("overridePresent")) {
      const title =
        "The attribute value has already been overridden. The change will not be applied.";
      return GrayValueWrapper(value.get("displayValue"), title);
    }
    if (
      !isEquivalentValue(value.get("displayValue"), incorrectValue, attribute)
    ) {
      const title = 'The value does not match the selected "Value to Change".';
      return GrayValueWrapper(value.get("displayValue"), title);
    }
    return value.get("displayValue");
  };

  const summaryDisplayProposedValue = (valueList, record) => {
    const value = valueList.find(
      v => v.get("source") + " | " + v.get("id") === record
    );
    switch (action) {
      case "replace":
        if (typeof value === "undefined") {
          const title =
            "The value to override does not exist. The change will not be applied.";
          return GrayValueWrapper("[No Change]", title);
        }
        if (value.get("use") === "old") {
          const title =
            "The value is inactive. The change will not be applied.";
          return GrayValueWrapper("[No Change]", title);
        }
        if (value.get("overridePresent")) {
          const title =
            "The attribute value has already been overridden. The change will not be applied.";
          return GrayValueWrapper("[No Change - Override Present]", title);
        }
        if (
          isEquivalentValue(value.get("displayValue"), correctValue, attribute)
        ) {
          const title =
            "The proposed value is already present. The change will not be applied.";
          return GrayValueWrapper("[No Change]", title);
        }
        if (
          !isEquivalentValue(
            value.get("displayValue"),
            incorrectValue,
            attribute
          )
        ) {
          const title =
            'The "Value to Change" does not match the current value. The change will not be applied.';
          return GrayValueWrapper("[No Change]", title);
        }
        return GreenValueWrapper(correctValue);

      case "inactive":
        if (typeof value === "undefined") {
          const title =
            "The value to inactivate does not exist. The change will not be applied.";
          return GrayValueWrapper("[No Change]", title);
        }
        if (value.get("overridePresent")) {
          const title =
            "The attribute value has already been overridden. The change will not be applied.";
          return GrayValueWrapper("[No Change - Override Present]", title);
        }
        if (
          !isEquivalentValue(
            value.get("displayValue"),
            incorrectValue,
            attribute
          )
        ) {
          const title =
            'The "Value to Change" does not match the current value. The change will not be applied.';
          return GrayValueWrapper("[No Change]", title);
        }
        if (value.get("use") === "old") {
          const title =
            "The value is already inactive. The change will not be applied.";
          return GrayValueWrapper("[No Change]", title);
        }
        return GreenValueWrapper("Value will be inactivated.");

      case "invalid":
        if (typeof value === "undefined") {
          const title =
            "The value to invalidate does not exist. The change will not be applied.";
          return GrayValueWrapper("[No Change]", title);
        }
        if (value.get("overridePresent")) {
          const title =
            "The attribute value has already been overridden. The change will not be applied.";
          return GrayValueWrapper("[No Change - Override Present]", title);
        }
        if (
          !isEquivalentValue(
            value.get("displayValue"),
            incorrectValue,
            attribute
          )
        ) {
          const title =
            'The "Value to Change" does not match the current value. The change will not be applied.';
          return GrayValueWrapper("[No Change]", title);
        }
        return GreenValueWrapper("Value will be invalidated.");
    }
  };

  const recordList = [
    ...new Set(
      selectedRecords.flat().map(v => v.get("source") + " | " + v.get("id"))
    )
  ];

  return (
    <div className={styles.overrideSummaryTable}>
      <table>
        <thead>
          <tr>
            <th>Selected Records</th>
            <th></th>
            <th style={{ minWidth: "10rem" }}>Current Value</th>
            <th></th>
            <th style={{ minWidth: "10rem" }}>Proposed New Value</th>
          </tr>
        </thead>
        <tbody>
          {recordList.map(record => (
            <tr style={{ height: "2rem" }}>
              <td>
                {summaryDisplayRecordId(
                  SummaryValueList(),
                  record,
                  action,
                  correctValue,
                  incorrectValue
                )}
              </td>
              <td></td>
              <td style={{ minWidth: "10rem" }}>
                {summaryDisplayCurrentValue(
                  SummaryValueList(),
                  record,
                  action,
                  correctValue,
                  incorrectValue
                )}
              </td>
              <td></td>
              <td style={{ minWidth: "10rem" }}>
                {summaryDisplayProposedValue(
                  SummaryValueList(),
                  record,
                  action,
                  correctValue,
                  incorrectValue
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SummaryTable;
